// First-login force-change screen for founders, served at /<cohortSlug>/founder/change-password.
// Gated by localStorage flag set at login time. The founder cannot reach
// the Command Center until they replace the temporary password.
import { useState } from "react";
import { useLocation } from "wouter";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { useCohort, fp } from "@/lib/founder-cohort";

const BASE = import.meta.env.BASE_URL;

export default function FounderChangePassword() {
  const [, navigate] = useLocation();
  const { slug } = useCohort();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 6) { setError("New password must be at least 6 characters"); return; }
    if (newPassword !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BASE}api/founders/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-founder-token": localStorage.getItem("founder_token") || "",
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || "Change failed");
        setLoading(false);
        return;
      }
      localStorage.removeItem("founder_must_change_password");
      setSuccess(true);
      setTimeout(() => navigate(fp(slug)), 1200);
    } catch (err: any) {
      setError(err?.message || "Network error");
      setLoading(false);
    }
  };

  return (
    <div className={/* ALLOW_HARDCODED_COLOR: dark anchor remapped by [data-founder-theme="light"] */ "min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6"}>
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold tracking-tight mb-1">Set a new password</h1>
          <p className="text-sm text-white/60">You're on a temporary password. Please choose your own to continue.</p>
        </div>

        {success ? (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
            <div className="font-semibold mb-1">Password updated.</div>
            <div className="text-sm text-white/60">Taking you to your command center…</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 bg-white/[0.04] border border-white/[0.1] rounded-xl p-6">
            {(["current", "new", "confirm"] as const).map((field) => {
              const value = field === "current" ? currentPassword : field === "new" ? newPassword : confirm;
              const setValue = field === "current" ? setCurrentPassword : field === "new" ? setNewPassword : setConfirm;
              const label = field === "current" ? "Current password" : field === "new" ? "New password" : "Confirm new password";
              return (
                <div key={field}>
                  <label className="block text-xs font-mono uppercase tracking-wider text-white/60 mb-2">{label}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                    <input
                      type={show ? "text" : "password"}
                      value={value}
                      onChange={e => setValue(e.target.value)}
                      required
                      className="w-full bg-white/[0.04] border border-white/[0.1] rounded-md pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#FF5C00]/60 focus:ring-1 focus:ring-[#FF5C00]/40"
                    />
                    {field === "current" && (
                      <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/70">
                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-50 text-white rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
